import { cloneElement, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import { Box } from '../../layout/Box';
import { DataTestId } from '../../layout/DataTestIdWrapper';
import { Typography } from '../../typography/Typography';
import { Item, Wrapper } from './styles';
import { SuggestProps } from './types';
import { formatTextDefault } from './utils';

export const Suggest: React.FC<SuggestProps> = ({
    items,
    visible,
    trigger,
    empty,
    isFullWidth,
    onClick,
    onClose,
    formatText = formatTextDefault,
}) => {
    const foundItems = Object.keys(items);
    const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);
    const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
    const { styles, attributes, update } = usePopper(referenceElement, popperElement, {
        placement: 'bottom-start',
    });
    const direction = attributes.popper?.['data-popper-placement'];
    const offsetTop = referenceElement?.offsetTop;
    const isTopDirection = direction?.includes('top');

    useEffect(() => {
        if (visible) {
            update?.();
        }
    }, [visible, update]);

    return (
        <>
            {cloneElement(trigger, { ref: setReferenceElement })}
            {visible
                ? createPortal(
                      <Box
                          onClick={onClose}
                          ref={setPopperElement}
                          zIndex={9999}
                          style={styles.popper}
                          {...attributes.popper}
                          width={isFullWidth ? referenceElement?.clientWidth : 'auto'}
                      >
                          <Wrapper
                              style={styles.offset}
                              display={visible && (foundItems.length || empty) ? 'flex' : 'none'}
                              mt={isTopDirection ? 0 : 1}
                              mb={isTopDirection && offsetTop ? offsetTop / 8 + 1 : 0}
                              maxHeight={{ xs: '200px', sm: '300px' }}
                              overflowY="auto"
                              dataTestId={DataTestId.INPUT_SUGGEST}
                          >
                              {foundItems.length > 0 ? (
                                  foundItems.map((item) => (
                                      <Item
                                          key={item}
                                          variant="body2"
                                          weight="regular"
                                          onClick={() => onClick(item)}
                                          cursor="pointer"
                                          dataTestId={DataTestId.INPUT_SUGGEST_ITEM}
                                      >
                                          {formatText(item, items[item])}
                                      </Item>
                                  ))
                              ) : (
                                  <Typography variant="body2" weight="regular" p={2}>
                                      {empty}
                                  </Typography>
                              )}
                          </Wrapper>
                      </Box>,
                      document.body,
                  )
                : null}
        </>
    );
};
